import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Award,
  BarChart3,
  Calendar,
  Dumbbell,
  Flag,
  Gift,
  Heart,
  Lock,
  MessageCircle,
  Shield,
  Sparkles,
  Target,
  Trophy,
  Users,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * /features — the full product surface, cardless editorial layout
 * per the frontend-skill rules. Each module is one row: kicker,
 * title, one short paragraph, a small artifact that proves the
 * feature exists. Sections in the order a member or trainer would
 * meet them in the product.
 */
// Artifact factories — declared before MODULES so the array below
// can reference them without hitting the temporal dead zone. We
// store `artifact: () => <X />` (a factory) rather than
// `artifact: <X />` (pre-evaluated JSX) because module-level JSX
// evaluation runs at import time, when these components are not
// yet defined, and the build tools hoist them but the runtime
// does not — leading to "Cannot access 'ic' before initialization"
// the moment the module is loaded.
const MODULES = [
  {
    key: 'workout-log',
    no: '01',
    icon: Dumbbell,
    title: 'Workout logging',
    summary:
      'Members log a finished workout in 30 seconds. Templates, freeform, or trainer-issued. Every set goes to the same ledger.',
    bullets: [
      '3500+ exercise catalogue with typeahead',
      'Free-text custom exercises for when the catalogue does not have it',
      'PR detection on every set, points resolved from your gym config',
      'Self-logged or trainer-issued — both flow through one write stream',
    ],
    artifact: () => <WorkoutLogArtifact />,
  },
  {
    key: 'sessions',
    no: '02',
    icon: Calendar,
    title: 'Sessions & attendance',
    summary:
      'Trainers issue sessions, members confirm. Calendar of who showed up, who needs a nudge, and where the day is heading.',
    bullets: [
      'Trainer creates a session, client confirms, status moves PENDING → CONFIRMED',
      'Confirmation window with a deadline surfaced in the app',
      'Per-client history visible from the trainer dashboard',
    ],
    artifact: () => <SessionArtifact />,
  },
  {
    key: 'points',
    no: '03',
    icon: Award,
    title: 'Points & level-ups',
    summary:
      'Every logged session, PR, streak, and challenge moves the member on the points curve. Configurable per gym, surfaced everywhere.',
    bullets: [
      'Point rules editable from /gamification (admin only)',
      'Daily and weekly caps on each rule prevent runaway point farming',
      'Level-up emails, in-app notifications, and SSE feed push',
    ],
    artifact: () => <PointsArtifact />,
  },
  {
    key: 'badges',
    no: '04',
    icon: Trophy,
    title: 'Badges',
    summary:
      'Configurable badges unlock on milestone criteria. Surfaced in profile, social feed, and trainer review.',
    bullets: [
      'Criteria-based: sessions count, points total, streak days, level reached',
      'Bonus points awarded on unlock, plus an in-app + email notification',
    ],
  },
  {
    key: 'leaderboards',
    no: '05',
    icon: BarChart3,
    title: 'Leaderboards',
    summary:
      'Live, role-aware leaderboards. Trainers see their clients. Members see their team. Admins see the whole floor.',
    bullets: [
      'Daily, weekly, and all-time windows',
      'Tied to the same ledger entries that award points — no double counting',
    ],
  },
  {
    key: 'challenges',
    no: '06',
    icon: Target,
    title: 'Team & individual challenges',
    summary:
      'Run a 30-day squat challenge, a team-vs-team plank hold, or a custom metric. Members see momentum climb in real time.',
    bullets: [
      'Custom scoring rule per challenge',
      'Team leaderboard view when the challenge is team-scoped',
    ],
  },
  {
    key: 'rewards',
    no: '07',
    icon: Gift,
    title: 'Rewards catalog',
    summary:
      'Members redeem earned points for rewards you define. Pending → approved → fulfilled, with email notifications at every step.',
    bullets: [
      'Admin-curated catalog, point prices set per reward',
      'Member redemption request flow with status tracking',
    ],
  },
  {
    key: 'social',
    no: '08',
    icon: MessageCircle,
    title: 'Social feed & direct messages',
    summary:
      'Members post PRs, session completions, and badges to a team feed. Direct messages are 1-to-1, with read receipts.',
    bullets: [
      'Feed surfaces events the system already knows about — no manual posting',
      'DMs scoped to trainer ↔ client and team members',
    ],
  },
  {
    key: 'teams',
    no: '09',
    icon: Users,
    title: 'Teams',
    summary:
      'Invite members to a team, run team challenges, publish a team leaderboard. Teams add a second axis to the leaderboard surface.',
    bullets: [
      'Invite-only with a one-tap accept',
      'Team leaderboards and team challenge enrollment both reuse the same challenge engine',
    ],
  },
  {
    key: 'progress',
    no: '10',
    icon: Heart,
    title: 'Progress & body composition',
    summary:
      'Weight, body fat, photos. The progress surface reads the same workout log + body metric stream and shows it back as a graph.',
    bullets: [
      'Body metrics with date, weight, body fat %',
      'Progress photos uploaded by the member, visible to their trainer',
    ],
  },
  {
    key: 'insights',
    no: '11',
    icon: Sparkles,
    title: 'Insights — muscle map, calendar heatmap, PR progression',
    summary:
      'See which muscles you trained this week, the calendar of your active days, and the chart of every PR since you started.',
    bullets: [
      'Muscle map highlights trained groups from your actual set list',
      'Calendar heatmap shows session density at a glance',
      'PR progression chart per exercise, with deltas vs prior best',
    ],
    artifact: () => <InsightsArtifact />,
  },
  {
    key: 'integrity',
    no: '12',
    icon: Lock,
    title: 'Tamper-proof ledger',
    summary:
      'Every point, badge, reward, and session is recorded in an append-only, hash-chained ledger. The integrity page verifies the chain end to end and flags any break.',
    bullets: [
      'SHA-256 hash chain, every entry references the previous entry hash',
      'Integrity page recomputes the chain and shows where (if anywhere) it broke',
      'Members can see their own chain. Admins see the full org chain. Auditors will love it.',
    ],
    artifact: () => <LedgerArtifact />,
    id: 'ledger',
  },
  {
    key: 'security',
    no: '13',
    icon: Shield,
    title: 'Authentication, security, self-host',
    summary:
      'Keycloak OIDC for auth, role-based access for admin/trainer/client, full data export, and a self-host path for gyms that outgrow the platform.',
    bullets: [
      'OIDC via Keycloak, with optional EU single-tenant hosting',
      'Member data exportable in standard formats',
      'Self-host the same codebase on your own infrastructure',
    ],
  },
  {
    key: 'integrity-flags',
    no: '14',
    icon: Flag,
    title: 'Suspicious activity review',
    summary:
      'GymGamify watches for patterns that suggest point farming or off-platform behavior. Flags surface in admin review, not in the public leaderboard.',
    bullets: [
      'Auto-flag on >10 session events in a single day',
      'Admin can mark flag as reviewed, dismissed, or confirmed',
    ],
  },
];

export const FeaturesPage = () => {
  const { t } = useTranslation();

  useEffect(() => {
    document.title = t('landing.seo.featuresTitle', {
      defaultValue: 'GymGamify — Features',
    });
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        'content',
        t('landing.seo.featuresDescription', {
          defaultValue:
            'Workout logging, sessions, points, badges, leaderboards, challenges, social feed, insights, and a tamper-proof ledger — the full operating surface, free forever, for every gym.',
        })
      );
    }
  }, [t]);

  return (
    <div className="layout-shell py-14 lg:py-20">
      <header className="max-w-4xl motion-enter">
        <span className="hero-kicker">
          {t('landingFeaturesPage.kicker', { defaultValue: 'Feature surface' })}
        </span>
        <h1 className="mt-6 editorial-title text-[var(--color-text)]">
          {t('landingFeaturesPage.title', {
            defaultValue: 'The full operating surface, in one product.',
          })}
        </h1>
        <p className="mt-5 max-w-2xl text-lg leading-8 text-[var(--color-text-muted)]">
          {t('landingFeatures.subtitle', {
            defaultValue:
              '14 modules that work together to run a gym. Toggle what you use, configure what you need, no upcharges. Free forever, for every gym.',
          })}
        </p>
      </header>

      <ol className="mt-14 space-y-10">
        {MODULES.map((m, idx) => (
          <li
            key={m.key}
            id={m.id}
            className="motion-enter"
            style={{ animationDelay: `${Math.min(idx * 60, 240)}ms` }}
          >
            <article className="editorial-panel relative overflow-hidden p-8 md:p-10">
              <div className="absolute right-6 top-6 text-[11px] font-semibold uppercase tracking-[0.22em] text-[var(--color-text-muted)]">
                {m.no}
              </div>
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-2xl border border-primary-200/15 bg-primary-200/10">
                  <m.icon className="h-4 w-4 text-primary-100" />
                </span>
                <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--color-text-muted)]">
                  Module
                </span>
              </div>
              <h2 className="mt-5 font-display text-3xl uppercase tracking-[0.04em] text-[var(--color-text)] md:text-4xl">
                {m.title}
              </h2>
              <p className="mt-4 max-w-2xl text-base leading-8 text-[var(--color-text-muted)]">
                {m.summary}
              </p>

              {m.bullets && (
                <ul className="mt-6 grid gap-2 text-sm text-[var(--color-text-muted)] sm:grid-cols-2">
                  {m.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-3">
                      <span className="mt-2 inline-block h-1 w-1 shrink-0 rounded-full bg-primary-300" />
                      <span className="leading-6">{b}</span>
                    </li>
                  ))}
                </ul>
              )}

              {m.artifact && (
                <div className="mt-8">{m.artifact()}</div>
              )}
            </article>
          </li>
        ))}
      </ol>

      {/* Final CTA — same voice as the homepage, single sentence. */}
      <section className="mt-16">
        <div className="flex flex-col items-start gap-6 border-t border-[var(--color-border)] pt-12 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[var(--color-text-muted)]">
              Spin it up
            </p>
            <p className="mt-3 max-w-xl font-display text-3xl uppercase leading-tight text-[var(--color-text)]">
              Open an account, run your first session through the ledger today.
            </p>
          </div>
          <Link to="/register" className="btn btn-primary whitespace-nowrap">
            Start your gym
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

// ----------------------------------------------------------------
// Artifacts — small, single-purpose visual elements that prove the
// feature exists. Cardless, monochromatic, accent on one detail.
// ----------------------------------------------------------------

// Exercise typeahead mock — proves the workout log has a real
// searchable catalogue, not a <select> with 200 entries.
const SAMPLE_EXERCISES = [
  { name: 'Back Squat', meta: 'quadriceps / glutes / barbell' },
  { name: 'Bulgarian Split Squat', meta: 'quadriceps / dumbbell' },
  { name: 'Romanian Deadlift', meta: 'hamstrings / barbell' },
  { name: 'Hip Thrust', meta: 'glutes / barbell' },
];
const WorkoutLogArtifact = () => {
  const [q, setQ] = useState('squat');
  const matches = SAMPLE_EXERCISES.filter((e) =>
    e.name.toLowerCase().includes(q.toLowerCase())
  );
  return (
    <div className="rounded-2xl border border-[var(--color-border)] bg-[rgba(6,12,10,0.5)] p-5 md:p-6">
      <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[var(--color-text-muted)]">
        /workout-log — exercise typeahead
      </p>
      <div className="mt-4">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="w-full rounded-xl border border-[var(--color-border)] bg-white/[0.03] px-4 py-2.5 text-sm text-[var(--color-text)] focus:border-primary-300 focus:outline-none"
          placeholder="Search exercises — try 'squat'…"
        />
        <ul className="mt-3 space-y-1">
          {matches.length === 0 && (
            <li className="rounded-lg border border-dashed border-[var(--color-border)] px-3 py-2 text-xs text-[var(--color-text-muted)]">
              No matches in the catalogue. You can type a custom name below.
            </li>
          )}
          {matches.map((e) => (
            <li
              key={e.name}
              className="flex items-center justify-between rounded-lg border border-[var(--color-border)] bg-white/[0.02] px-3 py-2 text-xs"
            >
              <span className="text-[var(--color-text)]">{e.name}</span>
              <span className="text-[10px] uppercase tracking-[0.18em] text-[var(--color-text-muted)]">
                {e.meta}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

// Session card mock — trainer-issued, awaiting client confirm.
const SessionArtifact = () => (
  <div className="rounded-2xl border border-[var(--color-border)] bg-[rgba(6,12,10,0.5)] p-5 md:p-6">
    <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[var(--color-text-muted)]">
      /sessions — pending confirmation
    </p>
    <div className="mt-4 space-y-2">
      {[
        { name: 'Lower Body · 60 min', who: 'Trainer → Alex', state: 'PENDING' },
        { name: 'HIIT · 45 min', who: 'Trainer → Mira', state: 'CONFIRMED' },
        { name: 'Mobility · 30 min', who: 'Trainer → You', state: 'PENDING' },
      ].map((s) => (
        <div
          key={s.name}
          className="flex items-center gap-4 rounded-xl border border-[var(--color-border)] bg-white/[0.02] px-3 py-2.5 text-xs"
        >
          <div className="flex-1">
            <p className="text-[var(--color-text)]">{s.name}</p>
            <p className="mt-0.5 text-[10px] uppercase tracking-[0.18em] text-[var(--color-text-muted)]">
              {s.who}
            </p>
          </div>
          <span
            className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] ${
              s.state === 'CONFIRMED'
                ? 'border border-primary-300/40 bg-primary-300/10 text-primary-200'
                : 'border border-amber-300/30 bg-amber-300/10 text-amber-200'
            }`}
          >
            {s.state}
          </span>
        </div>
      ))}
    </div>
  </div>
);

// Points curve — three sample members at different points totals.
const PointsArtifact = () => (
  <div className="rounded-2xl border border-[var(--color-border)] bg-[rgba(6,12,10,0.5)] p-5 md:p-6">
    <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[var(--color-text-muted)]">
      /points — this week
    </p>
    <div className="mt-4 space-y-3">
      {[
        { name: 'Alex', points: 142, level: 4, max: 200 },
        { name: 'Mira', points: 96, level: 3, max: 200 },
        { name: 'You', points: 78, level: 2, max: 200 },
      ].map((u) => (
        <div key={u.name} className="flex items-center gap-3 text-xs">
          <span className="w-12 shrink-0 text-[var(--color-text)]">{u.name}</span>
          <div className="relative h-2 flex-1 overflow-hidden rounded-full bg-white/[0.06]">
            <div
              className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-primary-500 to-primary-300"
              style={{ width: `${(u.points / u.max) * 100}%` }}
            />
          </div>
          <span className="w-16 shrink-0 text-right text-[var(--color-text-muted)]">
            {u.points} pts
          </span>
          <span className="w-12 shrink-0 text-right text-[10px] uppercase tracking-[0.18em] text-primary-200">
            L{u.level}
          </span>
        </div>
      ))}
    </div>
  </div>
);

// Insights — muscle map + calendar heatmap as a single tile, with
// PR progression sparkline.
const InsightsArtifact = () => (
  <div className="grid gap-3 md:grid-cols-3">
    {/* Muscle map */}
    <div className="rounded-2xl border border-[var(--color-border)] bg-[rgba(6,12,10,0.5)] p-4">
      <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[var(--color-text-muted)]">
        muscle map · this week
      </p>
      <div className="mt-4 flex justify-center">
        <svg viewBox="0 0 80 96" className="h-32 w-24" aria-hidden="true">
          {/* simplified silhouette */}
          <circle cx="40" cy="14" r="8" className="fill-white/10" />
          <path
            d="M22 24 L58 24 L62 50 L48 50 L46 90 L34 90 L32 50 L18 50 Z"
            className="fill-white/10"
          />
          {/* trained muscle highlights */}
          <ellipse cx="28" cy="38" rx="6" ry="10" className="fill-primary-300/80" />
          <ellipse cx="52" cy="38" rx="6" ry="10" className="fill-primary-300/80" />
          <ellipse cx="40" cy="68" rx="10" ry="6" className="fill-primary-300/40" />
        </svg>
      </div>
      <p className="mt-2 text-center text-[10px] uppercase tracking-[0.18em] text-[var(--color-text-muted)]">
        chest + quads trained
      </p>
    </div>

    {/* Calendar heatmap */}
    <div className="rounded-2xl border border-[var(--color-border)] bg-[rgba(6,12,10,0.5)] p-4">
      <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[var(--color-text-muted)]">
        calendar · 30 days
      </p>
      <div className="mt-4 grid grid-cols-10 gap-1">
        {Array.from({ length: 30 }).map((_, i) => {
          // pseudo-random intensity
          const level = (i * 7 + 3) % 5;
          const color =
            level === 0
              ? 'bg-white/5'
              : level === 1
              ? 'bg-primary-300/20'
              : level === 2
              ? 'bg-primary-300/40'
              : level === 3
              ? 'bg-primary-300/70'
              : 'bg-primary-300';
          return <span key={i} className={`h-3 rounded ${color}`} />;
        })}
      </div>
      <p className="mt-3 text-[10px] uppercase tracking-[0.18em] text-[var(--color-text-muted)]">
        18 active days
      </p>
    </div>

    {/* PR progression */}
    <div className="rounded-2xl border border-[var(--color-border)] bg-[rgba(6,12,10,0.5)] p-4">
      <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[var(--color-text-muted)]">
        PR · back squat (kg)
      </p>
      <svg viewBox="0 0 100 40" className="mt-3 h-24 w-full" aria-hidden="true">
        <polyline
          points="0,32 14,30 28,26 42,22 56,18 70,16 84,12 100,8"
          fill="none"
          stroke="var(--color-primary-300)"
          strokeWidth="1.5"
        />
        <polyline
          points="0,32 14,30 28,26 42,22 56,18 70,16 84,12 100,8 100,40 0,40"
          fill="var(--color-primary-300)"
          fillOpacity="0.1"
          stroke="none"
        />
      </svg>
      <p className="mt-1 text-[10px] uppercase tracking-[0.18em] text-primary-200">
        60 → 100 kg over 6 PRs
      </p>
    </div>
  </div>
);

// Ledger artifact — same visual primitive as the home page so the
// two pages speak the same language about the ledger.
const LedgerArtifact = () => {
  const blocks = [
    { idx: '#3821', label: 'session_logged', delta: '+5 pts' },
    { idx: '#3822', label: 'pr_recorded', delta: '+25 pts' },
    { idx: '#3823', label: 'streak_7d', delta: '+50 pts' },
    { idx: '#3824', label: 'badge_unlocked', delta: '+10 pts' },
    { idx: '#3825', label: 'session_logged', delta: '+5 pts', active: true },
  ];
  return (
    <div className="rounded-2xl border border-[var(--color-border)] bg-[rgba(6,12,10,0.5)] p-5 md:p-6">
      <div className="flex items-center justify-between border-b border-[var(--color-border)] pb-4">
        <div className="flex items-center gap-2">
          <Lock className="h-4 w-4 text-primary-200" />
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--color-text)]">
            event_ledger · last 5 entries
          </p>
        </div>
        <p className="text-[10px] uppercase tracking-[0.22em] text-[var(--color-text-muted)]">
          append-only · sha-256
        </p>
      </div>
      <ul className="mt-4 space-y-2">
        {blocks.map((b) => (
          <li
            key={b.idx}
            className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 text-xs ${
              b.active
                ? 'border-primary-300/40 bg-primary-300/10 text-[var(--color-text)]'
                : 'border-[var(--color-border)] bg-white/[0.02] text-[var(--color-text-muted)]'
            }`}
          >
            <span
              className={`font-mono text-[10px] ${
                b.active ? 'text-primary-200' : 'text-[var(--color-text-muted)]'
              }`}
            >
              {b.idx}
            </span>
            <span
              className={`flex-1 truncate font-mono text-[11px] ${
                b.active ? 'text-[var(--color-text)]' : ''
              }`}
            >
              {b.label}
            </span>
            <span
              className={`text-[11px] ${
                b.active ? 'text-primary-200' : 'text-[var(--color-text-muted)]'
              }`}
            >
              {b.delta}
            </span>
          </li>
        ))}
      </ul>
      <div className="mt-4 flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-[var(--color-text-muted)]">
        <span className="font-mono">prev_hash: 8f4e…b21c</span>
        <span>→</span>
        <span className="font-mono text-primary-200">hash: c7a9…4e0f</span>
      </div>
      <p className="mt-4 text-[10px] uppercase tracking-[0.18em] text-[var(--color-text-muted)]">
        /integrity recomputes the chain and flags any break
      </p>
    </div>
  );
};

export default FeaturesPage;
